import React, {
    useState,
    useEffect,
    useRef,
    useImperativeHandle,
    forwardRef,
    useCallback,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import {
    Paper,
    Text,
    Button,
    Flex,
    Stack,
    ScrollArea,
    Group,
    Box,
    ActionIcon,
} from '@mantine/core';
import { createStyles } from '@mantine/styles';
import { keyframes } from '@emotion/react';
import {
    IconChevronLeft,
    IconX,
    IconLayoutSidebar,
    IconLayoutSidebarRight,
    IconChevronDown,
} from '@tabler/icons-react';
import ChatLeftSection from '@/features/ai/chat_window/ChatLeftSection';
import ChatRightSection from '@/features/ai/chat_window/ChatRightSection';
import { useAuthStore } from '@/stores/authStore';
import {
    UserMessageBubble,
    AiMessageBubble,
} from '@/features/ai/chat_window/MessageBubble';
import { ActiveToolsList } from '@/features/ai/chat_window/ToolUseBox';
import { parseToolUseMessage } from '@/features/ai/chat_window/utils/parseToolMessage';
// import ReportCreationResult from '@/features/ai/chat_window/ReportCreationResult';
import { defaultAiRequestManager } from '@/features/ai/components/McpAI/defaultAiRequestManager';

// useStyles 정의
const blinkAnimation = keyframes({
    '0%': { opacity: 0.4 },
    '50%': { opacity: 1 },
    '100%': { opacity: 0.4 },
});

const useStyles = createStyles((theme, _params, getRef) => ({
    chatWindow: {
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        width: '800px',
        height: '620px',
        zIndex: 1001,
        borderRadius: '16px',
        backgroundColor: theme.white,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '-8px 0px 12px rgba(0, 0, 0, 0.8)',
    },
    chatHeaderLeft: {
        width: '318px',
        backgroundColor: theme.colors.gray[0], // #f8f9fa
        boxSizing: 'border-box',
        flexShrink: 0,
    },
    chatHeaderRight: {
        width: '482px',
        backgroundColor: theme.white, // #FFFFFF
        boxSizing: 'border-box',
        flexShrink: 0,
    },
    chatHeaderLeftClosed: {
        width: '56px',
        backgroundColor: theme.colors.gray[0], // #f8f9fa
        boxSizing: 'border-box',
        flexShrink: 0,
    },
    chatHeaderRightClosed: {
        width: '744px',
        backgroundColor: theme.white, // #FFFFFF
        boxSizing: 'border-box',
        flexShrink: 0,
    },
    chatMessagesContainer: {
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: '0%',
        minHeight: 0,
        padding: theme.spacing.md, // 16px
        overflowY: 'auto',
        height: 'calc(100% - 56px)', // 헤더 높이를 뺀 높이로 설정 (56px: 헤더 높이)
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
    },
    chatMessageTools: {
        marginTop: -2,
        marginLeft: 4,
        position: 'relative',
        zIndex: 1,
        width: '80%',
        display: 'flex',
        justifyContent: 'flex-start',
    },
    reportCreationContainer: {
        marginTop: -8,
        marginLeft: 40,
        width: '80%',
        maxWidth: 500,
        position: 'relative',
        zIndex: 2,
    },
    thinkingContainer: {
        marginLeft: 40,
        marginBottom: theme.spacing.xs, // 10px
        marginTop: theme.spacing.xs, // 8px
        position: 'relative',
    },
    thinkingDot: {
        display: 'inline-block',
        marginLeft: 2,
        animation: `${blinkAnimation} 1.4s infinite`,
        '&:nth-of-type(2)': {
            animationDelay: '0.2s',
        },
        '&:nth-of-type(3)': {
            animationDelay: '0.4s',
        },
    },
}));

interface ChatMessage {
    content: string;
    isUser: boolean;
    timestamp: Date;
    associatedTools?: ToolInfo[]; // 각 메시지와 연관된 도구 목록
    metadata?: Record<string, unknown>; // 메시지와 관련된 추가 메타데이터 (날짜 정보 등)
}

// 도구 사용 정보 인터페이스
interface ToolInfo {
    name: string;
    input?: Record<string, unknown>;
    isCompleted?: boolean;
    specialType?: string;
    showReportFormat?: boolean; // 보고서 형식 표시 여부
    showAfterMessage?: boolean; // AI 메시지 이후에 표시할지 여부
    messageId?: string; // messageId 추가
}

// ChatWindow 핸들러 타입 정의
export interface ChatWindowHandlers {
    addAiMessage: (content: string, isNewMessage?: boolean) => void;
    addErrorMessage: (errorMessage: string) => void;
    setLoadingState: (loading: boolean) => void;
    addToolUse?: (toolInfo: ToolInfo) => void;
    completeToolUse?: (toolName: string) => void;
    resetToolStates?: () => void; // 도구 상태 초기화 함수 추가
}

interface ChatWindowProps {
    isOpen: boolean;
    onClose: () => void;
    onSendMessage?: (message: string) => void;
}

// forwardRef를 사용하여 함수를 외부로 노출
const ChatWindow = forwardRef<ChatWindowHandlers, ChatWindowProps>(
    ({ isOpen, onClose, onSendMessage }, ref): JSX.Element => {
        const { t } = useTranslation('ai');
        const { classes } = useStyles(); // useStyles hook 호출
        const [messages, setMessages] = useState<ChatMessage[]>([]);
        const [inputValue, setInputValue] = useState('');
        const [isLoading, setIsLoading] = useState(false);
        const [isThinking, setIsThinking] = useState(false); // AI가 생각 중인지 상태 추가
        const [activeTools, setActiveTools] = useState<ToolInfo[]>([]);
        const [reportFormatTools, setReportFormatTools] = useState<ToolInfo[]>(
            []
        );
        const [selectedFeature, setSelectedFeature] = useState<string | null>(
            null
        );
        const [leftSectionCollapsed, setLeftSectionCollapsed] = useState(true); // 왼쪽 섹션 접힘 상태

        // 스크롤 상태 관리 추가
        const [isUserScrolling, setIsUserScrolling] = useState(false);
        const [isNearBottom, setIsNearBottom] = useState(true);
        const [showScrollToBottom, setShowScrollToBottom] = useState(false);

        const { user } = useAuthStore();
        const scrollAreaRef = useRef<HTMLDivElement>(null);

        // 채팅 세션 ID 생성
        // const sessionIdRef = useRef<string>(uuidv4());

        // 스크롤 관련 상태 관리를 위한 ref
        const lastUserScrollTimeRef = useRef<number>(Date.now());
        const userScrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
        const lastMessageCountRef = useRef<number>(0); // 마지막 메시지 개수 추적
        const isStreamingRef = useRef<boolean>(false); // 토큰 스트리밍 중인지 여부

        // 스크롤 이벤트 핸들러
        const handleScroll = () => {
            if (scrollAreaRef.current) {
                const { scrollTop, scrollHeight, clientHeight } =
                    scrollAreaRef.current;
                const distanceFromBottom =
                    scrollHeight - scrollTop - clientHeight;
                const nearBottomThreshold = 100;

                const isNear = distanceFromBottom <= nearBottomThreshold;
                setIsNearBottom(isNear);

                // 현재 시간 기록
                const now = Date.now();

                // 사용자 스크롤 감지 - 위로 스크롤한 경우에만 사용자 스크롤로 간주
                if (
                    !isNear &&
                    !isUserScrolling &&
                    scrollTop < scrollHeight - clientHeight - 200
                ) {
                    // 마지막 스크롤 시간 업데이트
                    lastUserScrollTimeRef.current = now;
                    console.log(
                        '[ChatWindow] 사용자 수동 스크롤 감지 - 거리:',
                        distanceFromBottom
                    );
                    setIsUserScrolling(true);

                    // 이전 타임아웃이 있으면 취소
                    if (userScrollTimeoutRef.current) {
                        clearTimeout(userScrollTimeoutRef.current);
                        userScrollTimeoutRef.current = null;
                    }
                }

                // 사용자가 명시적으로 하단까지 스크롤한 경우에만 자동 스크롤 재개
                // 토큰 스트리밍 중이 아닐 때만 하단 도달시 자동 스크롤 재개
                if (isNear && isUserScrolling && !isStreamingRef.current) {
                    console.log('[ChatWindow] 하단 복귀로 자동 스크롤 재개');
                    setIsUserScrolling(false);
                }
            }
        };

        // 휠 이벤트 핸들러 추가 - React.WheelEventHandler 타입 사용
        const handleWheel = React.useCallback(
            (event: React.WheelEvent<HTMLDivElement>) => {
                // 휠 이벤트가 발생하면 사용자가 스크롤 중인 것으로 간주
                if (event.deltaY < 0) {
                    // 위로 스크롤하는 경우
                    console.log('[ChatWindow] 휠 이벤트로 사용자 스크롤 감지');
                    setIsUserScrolling(true);

                    // 이전 타임아웃이 있으면 취소
                    if (userScrollTimeoutRef.current) {
                        clearTimeout(userScrollTimeoutRef.current);
                        userScrollTimeoutRef.current = null;
                    }

                    // 스크롤 상태를 유지하기 위한 타이머 설정
                    userScrollTimeoutRef.current = setTimeout(() => {
                        // 3초 동안 추가 스크롤이 없으면 자동 스크롤 상태 확인
                        if (scrollAreaRef.current) {
                            const { scrollTop, scrollHeight, clientHeight } =
                                scrollAreaRef.current;
                            const distanceFromBottom =
                                scrollHeight - scrollTop - clientHeight;

                            // 하단에 가까운 경우에만 자동 스크롤 재개
                            if (
                                distanceFromBottom <= 70 &&
                                !isStreamingRef.current
                            ) {
                                console.log(
                                    '[ChatWindow] 타임아웃 후 하단 근처 확인, 자동 스크롤 재개'
                                );
                                setIsUserScrolling(false);
                            }
                        }
                    }, 3000); // 3초 타임아웃
                }
            },
            []
        );

        // 메시지 개수 변화 감지
        useEffect(() => {
            // 메시지 개수가 증가했는지 확인 (새 메시지 추가)
            const isNewMessage = messages.length > lastMessageCountRef.current;
            lastMessageCountRef.current = messages.length;

            // 새 메시지가 추가된 경우에만 스크롤 자동 이동 고려
            if (isNewMessage) {
                console.log(
                    '[ChatWindow] 새 메시지 추가 감지:',
                    messages.length
                );

                // 사용자가 수동으로 스크롤 중이 아니거나, 하단 근처에 있을 때만 자동 스크롤
                if (
                    (!isUserScrolling || isNearBottom) &&
                    scrollAreaRef.current
                ) {
                    setTimeout(() => {
                        if (
                            scrollAreaRef.current &&
                            (!isUserScrolling || isNearBottom)
                        ) {
                            scrollAreaRef.current.scrollTop =
                                scrollAreaRef.current.scrollHeight;
                            console.log(
                                '[ChatWindow] 새 메시지 추가로 인한 자동 스크롤 실행'
                            );
                        }
                    }, 100);
                } else {
                    console.log(
                        '[ChatWindow] 사용자 스크롤 중이므로 자동 스크롤 생략'
                    );
                }
            }
        }, [messages.length, isUserScrolling, isNearBottom]);

        // 토큰 스트리밍 중 스크롤 처리
        useEffect(() => {
            // 마지막 메시지가 AI 메시지이고 내용이 변경된 경우 토큰 스트리밍 중으로 간주
            if (messages.length > 0 && !messages[messages.length - 1].isUser) {
                isStreamingRef.current = true;

                // 사용자가 스크롤 중이 아니거나 하단에 있을 때만 스크롤
                if (
                    (!isUserScrolling || isNearBottom) &&
                    scrollAreaRef.current
                ) {
                    scrollAreaRef.current.scrollTop =
                        scrollAreaRef.current.scrollHeight;
                }

                // 토큰 스트리밍 종료 시간 설정 (1초 후 스트리밍 상태 해제)
                const streamingTimeout = setTimeout(() => {
                    isStreamingRef.current = false;
                    console.log('[ChatWindow] 토큰 스트리밍 종료');
                }, 1000);

                return () => clearTimeout(streamingTimeout);
            }
        }, [messages, activeTools, isUserScrolling, isNearBottom]);

        // ReportCreationResult 표시 조건 로그 출력
        useEffect(() => {
            if (messages.length > 0) {
                const lastMessage = messages[messages.length - 1];
                if (!lastMessage.isUser) {
                    console.log(
                        `%c[로그 확인] ReportCreationResult 표시 조건 검사`,
                        'background: #f0f0f0; color: #333; font-weight: bold; padding: 2px 5px; border-radius: 3px;'
                    );
                    console.log(`[로그 확인] 마지막 메세지는 AI 메세지`);
                    console.log(
                        `[로그 확인] reportFormatTools:`,
                        reportFormatTools
                    );
                    console.log(
                        `[로그 확인] reportFormatTools 상세:`,
                        reportFormatTools.map(t => ({
                            name: t.name,
                            showAfterMessage: t.showAfterMessage,
                            isCompleted: t.isCompleted,
                            input: t.input ? Object.keys(t.input) : [],
                        }))
                    );

                    const willShowReportCreation = reportFormatTools.some(
                        tool => tool.showAfterMessage
                    );
                    console.log(
                        `%c[로그 확인] ReportCreationResult 표시 여부: ${willShowReportCreation}`,
                        willShowReportCreation
                            ? 'background: #4CAF50; color: white; font-weight: bold; padding: 2px 5px; border-radius: 3px;'
                            : 'background: #f44336; color: white; font-weight: bold; padding: 2px 5px; border-radius: 3px;'
                    );

                    if (willShowReportCreation) {
                        const activeTool = reportFormatTools.find(
                            tool => tool.showAfterMessage
                        );
                        console.log(
                            `%c[로그 확인] 표시될 보고서 도구:`,
                            'background: #2196F3; color: white; font-weight: bold; padding: 2px 5px; border-radius: 3px;',
                            {
                                name: activeTool?.name,
                                input: activeTool?.input,
                                reportType: activeTool.name,
                                // 보고서 생성 시 사용자가 선택한 날짜 정보를 포함한 reportData 전달
                                reportData: {
                                    ...activeTool.input,
                                    // 마지막 사용자 메시지에 메타데이터가 있으면 해당 정보 추가
                                    selectedDate:
                                        messages.length > 0 &&
                                        messages[messages.length - 2]?.metadata
                                            ?.selectedDate,
                                    selectedDateRange:
                                        messages.length > 0 &&
                                        messages[messages.length - 2]?.metadata
                                            ?.selectedDateRange,
                                    ...(messages.length > 0 &&
                                    messages[messages.length - 2]?.metadata
                                        ? messages[messages.length - 2].metadata
                                        : {}),
                                },
                                content: lastMessage?.content || '',
                                // CreateReport에서 선택한 워크스페이스 ID 사용 (무조건 선택해야 함)
                                workspaceId:
                                    messages.length > 0 &&
                                    messages[messages.length - 2]?.metadata
                                        ?.selectedWorkspace
                                        ? (messages[messages.length - 2]
                                              .metadata
                                              .selectedWorkspace as string)
                                        : '',
                                isCompleted: activeTool?.isCompleted,
                                showAfterMessage: activeTool?.showAfterMessage,
                            }
                        );
                    }
                }
            }
        }, [messages, reportFormatTools]);

        // 도구 사용 이벤트 리스너 등록
        useEffect(() => {
            // 도구 사용 시작 이벤트 핸들러
            const handleToolUseStart = (event: Event) => {
                const customEvent = event as CustomEvent<{
                    toolId: string;
                    toolName: string;
                    input: Record<string, unknown>;
                }>;

                const { toolName, input } = customEvent.detail;
                console.log(
                    '[ChatWindow] 도구 사용 시작 이벤트 수신:',
                    toolName,
                    {
                        inputKeys: Object.keys(input || {}),
                        messagesCount: messages.length,
                    }
                );

                // ChatWindow의 addToolUse 함수 호출하여 도구 정보 추가
                addToolUse({
                    name: toolName,
                    input,
                    isCompleted: false,
                });
            };

            // 도구 사용 완료 이벤트 핸들러
            const handleToolUseComplete = (event: Event) => {
                const customEvent = event as CustomEvent<{
                    toolId: string;
                    toolName: string;
                }>;

                const { toolName } = customEvent.detail;
                console.log(
                    '[ChatWindow] 도구 사용 완료 이벤트 수신:',
                    toolName
                );

                if (!toolName) {
                    console.log('[ChatWindow] 도구 이름이 없어 완료 처리 불가');
                    return;
                }

                // ChatWindow의 completeToolUse 함수 호출하여 도구 완료 처리
                completeToolUse(toolName);
            };

            // 이벤트 리스너 등록
            window.addEventListener('tool-use-start', handleToolUseStart);
            window.addEventListener('tool-use-complete', handleToolUseComplete);

            // 디버깅용 로그 출력
            console.log('[ChatWindow] 도구 사용 이벤트 리스너 등록됨');

            // 컴포넌트 언마운트 시 이벤트 리스너 제거
            return () => {
                window.removeEventListener(
                    'tool-use-start',
                    handleToolUseStart
                );
                window.removeEventListener(
                    'tool-use-complete',
                    handleToolUseComplete
                );
                console.log('[ChatWindow] 도구 사용 이벤트 리스너 제거됨');
            };
        }, [messages.length]);

        // 도구 사용 추가 함수
        const addToolUse = (toolInfo: ToolInfo) => {
            console.log('[ChatWindow] 도구 사용 추가:', toolInfo, {
                name: toolInfo.name,
                specialType: toolInfo.specialType,
                isCompleted: toolInfo.isCompleted,
            });

            // 노트 검색 결과와 같은 특수 타입의 도구 처리
            if (toolInfo.specialType === 'note_search_result') {
                console.log('[ChatWindow] 노트 검색 결과 특수 처리:', toolInfo);
            }

            // 보고서 형식 도구인지 확인
            const isReportFormatTool =
                toolInfo.name.includes('daily_report_format') ||
                toolInfo.name.includes('weekly_report_format');
            if (isReportFormatTool) {
                console.log(
                    '%c[ChatWindow] 보고서 형식 도구 추가 (상태 저장):',
                    'background: #9c27b0; color: white; font-weight: bold; padding: 2px 5px; border-radius: 3px;',
                    {
                        name: toolInfo.name,
                        input: toolInfo.input
                            ? Object.keys(toolInfo.input)
                            : [],
                        isCompleted: toolInfo.isCompleted,
                    }
                );

                // ReportCreationResult 렌더링을 위해 reportFormatTools 상태에 저장
                setReportFormatTools(prev => {
                    // 동일 도구가 이미 있는지 확인하여 중복 방지
                    const exists = prev.some(
                        tool => tool.name === toolInfo.name
                    );
                    if (exists) {
                        console.log(
                            '[ChatWindow] 이미 존재하는 보고서 도구, 무시:',
                            toolInfo.name
                        );
                        return prev;
                    }

                    console.log(
                        '[ChatWindow] 새 보고서 도구 추가:',
                        toolInfo.name,
                        '(showAfterMessage: false)'
                    );
                    return [
                        ...prev,
                        {
                            ...toolInfo,
                            showReportFormat: false,
                            showAfterMessage: false, // 초기에는 반드시 false로 설정
                        },
                    ];
                });
                // 중요: 여기서 return 하지 않고 아래 로직으로 넘어가 activeTools에도 추가합니다.
            }

            // 보고서 형식 도구를 포함한 모든 도구를 activeTools에 추가하여 ToolUseBox 즉시 렌더링
            setActiveTools(prev => {
                // 새 도구 추가 전에 기존 미완료 도구들을 완료 처리
                const completedPrevTools = prev.map(tool => {
                    if (!tool.isCompleted) {
                        console.log(
                            '[ChatWindow] 이전 미완료 도구 자동 완료:',
                            tool.name
                        );
                        return { ...tool, isCompleted: true };
                    }
                    return tool;
                });

                // 이미 같은 이름의 도구가 activeTools에 있는지 확인 (중복 방지)
                const exists = completedPrevTools.some(
                    tool => tool.name === toolInfo.name
                );
                if (exists) {
                    console.log(
                        '[ChatWindow] 이미 존재하는 도구(activeTools), 무시:',
                        toolInfo.name
                    );
                    return completedPrevTools;
                }

                console.log('[ChatWindow] Active Tool 추가:', toolInfo.name);
                return [
                    ...completedPrevTools,
                    { ...toolInfo, isCompleted: false },
                ];
            });

            // 마지막 AI 메시지의 associatedTools 배열에 도구 추가 및 기존 도구 완료 처리
            setMessages(prev => {
                // 메시지가 없거나 마지막 메시지가 사용자 메시지인 경우 변경하지 않음
                if (prev.length === 0 || prev[prev.length - 1].isUser) {
                    console.log(
                        '[ChatWindow] 메시지가 없거나 마지막이 사용자 메시지, 도구 연결 안 함'
                    );
                    return prev;
                }

                return prev.map((msg, index) => {
                    // 모든 메시지의 미완료 도구들을 완료 처리
                    if (msg.associatedTools && msg.associatedTools.length > 0) {
                        const updatedTools = msg.associatedTools.map(tool => {
                            if (
                                !tool.isCompleted &&
                                tool.name !== toolInfo.name
                            ) {
                                console.log(
                                    '[ChatWindow] 메시지 관련 미완료 도구 자동 완료:',
                                    tool.name
                                );
                                return { ...tool, isCompleted: true };
                            }
                            return tool;
                        });

                        msg = { ...msg, associatedTools: updatedTools };
                    }

                    // 마지막 AI 메시지에 새 도구 연결
                    if (index === prev.length - 1 && !msg.isUser) {
                        const updatedTools = [
                            ...(msg.associatedTools || []),
                            toolInfo,
                        ];
                        console.log(
                            '[ChatWindow] 마지막 AI 메시지에 도구 연결:',
                            {
                                messageIndex: index,
                                messageContent:
                                    msg.content.substring(0, 30) + '...',
                                prevToolsCount:
                                    msg.associatedTools?.length || 0,
                                newToolsCount: updatedTools.length,
                                toolNames: updatedTools.map(t => t.name),
                            }
                        );
                        return {
                            ...msg,
                            associatedTools: updatedTools,
                        };
                    }

                    return msg;
                });
            });
        };

        // AI 메시지 추가 함수 (외부에서 호출)
        const addAiMessage = (content: string, isNewMessage = false) => {
            console.log(
                '[ChatWindow] AI 메시지 추가:',
                content.substring(0, 50) + (content.length > 50 ? '...' : ''),
                isNewMessage ? '(새 메시지)' : ''
            );

            // 빈 메시지는 처리하지 않음
            if (!content || content.trim() === '') {
                console.log('[ChatWindow] 빈 메시지 무시');
                return;
            }

            // AI 메시지를 받으면 로딩 상태와 생각 중 상태 해제
            setIsLoading(false);
            setIsThinking(false); // 첫 청크를 받으면 생각 중 상태 해제

            // 로딩 상태 변경 이벤트 발생
            const loadingStateEvent = new CustomEvent(
                'ai-loading-state-changed',
                {
                    detail: { isLoading: false },
                }
            );
            window.dispatchEvent(loadingStateEvent);

            // 새 메시지가 추가될 때 모든 미완료 도구를 완료 처리
            if (isNewMessage) {
                // 활성 도구들을 모두 완료 상태로 변경 (report_format 도구 제외)
                setActiveTools(prev => {
                    const allCompleted = prev.map(tool => {
                        // 보고서 형식 도구는 완료 처리하지 않고 유지
                        if (
                            !tool.isCompleted &&
                            !tool.name.includes('daily_report_format') &&
                            !tool.name.includes('weekly_report_format')
                        ) {
                            console.log(
                                '[ChatWindow] 새 메시지로 인한 도구 자동 완료:',
                                tool.name
                            );
                            return { ...tool, isCompleted: true };
                        }
                        return tool;
                    });
                    return allCompleted;
                });

                // 같은 방식으로 메시지 도구 처리에도 예외 추가
                setMessages(prev =>
                    prev.map(msg => {
                        if (
                            msg.associatedTools &&
                            msg.associatedTools.length > 0
                        ) {
                            const updatedTools = msg.associatedTools.map(
                                tool => {
                                    if (
                                        !tool.isCompleted &&
                                        !tool.name.includes(
                                            'daily_report_format'
                                        ) &&
                                        !tool.name.includes(
                                            'weekly_report_format'
                                        )
                                    ) {
                                        console.log(
                                            '[ChatWindow] 새 메시지로 인한 메시지 도구 자동 완료:',
                                            tool.name
                                        );
                                        return { ...tool, isCompleted: true };
                                    }
                                    return tool;
                                }
                            );

                            return {
                                ...msg,
                                associatedTools: updatedTools,
                            };
                        }
                        return msg;
                    })
                );
            }

            // 도구 사용 메시지인지 확인
            const isToolUseMessage = false;

            try {
                const toolInfo = parseToolUseMessage(content);
                if (toolInfo) {
                    console.log(
                        '[ChatWindow] 도구 사용 메시지 감지:',
                        toolInfo.name,
                        {
                            specialType: toolInfo.specialType,
                            hasInput: !!toolInfo.input,
                            inputKeys: toolInfo.input
                                ? Object.keys(toolInfo.input)
                                : [],
                        }
                    );

                    // unknown_json + success: true + status: OK + todo_id 패턴이면 create_todo로 변환
                    if (
                        toolInfo.name === 'unknown_json' &&
                        toolInfo.input &&
                        toolInfo.input?.success === true &&
                        toolInfo.input?.status === 'OK' &&
                        toolInfo.input?.todo_id &&
                        typeof toolInfo.input?.todo_id === 'string'
                    ) {
                        console.log(
                            '[ChatWindow] unknown_json을 create_todo로 변환: success/status/todo_id 패턴 감지'
                        );
                        toolInfo.name = 'create_todo';
                    } else if (
                        toolInfo.name === 'unknown_json' &&
                        toolInfo.input?.data &&
                        Array.isArray(toolInfo.input?.data) &&
                        toolInfo.input?.data?.length > 0
                    ) {
                        // data 배열 내부에 todo_id가 있는지 확인
                        const dataItem = toolInfo.input?.data?.[0] as Record<
                            string,
                            unknown
                        >;
                        if (
                            dataItem?.todo_id &&
                            typeof dataItem?.todo_id === 'string'
                        ) {
                            console.log(
                                '[ChatWindow] unknown_json을 create_todo로 변환: data 배열에서 todo_id 패턴 감지'
                            );
                            toolInfo.name = 'create_todo';
                            // toolInput에 직접 todo_id 추가
                            toolInfo.input.todo_id = dataItem.todo_id;
                        }
                    }
                }
            } catch (error) {
                // 도구 사용 메시지가 아니면 무시하고 계속 진행
                console.log('[ChatWindow] 도구 사용 메시지 파싱 오류:', error);
            }

            console.log('[ChatWindow] 일반 텍스트 메시지로 처리');

            // 일반 텍스트 메시지 처리
            const botMessage: ChatMessage = {
                content,
                isUser: false,
                timestamp: new Date(),
            };

            setMessages(prev => {
                // 마지막 메시지 확인
                const lastMessage = prev[prev.length - 1];

                // 도구 사용 후 메시지면 항상 새 메시지 생성
                if (isNewMessage) {
                    console.log(
                        '[ChatWindow] 도구 사용 후 메시지 - 새 AI 메시지 생성',
                        {
                            isNewMessage,
                            contentPreview:
                                content.substring(0, 50) +
                                (content.length > 50 ? '...' : ''),
                        }
                    );
                    return [...prev, botMessage];
                }

                // 마지막 메시지가 AI 메시지이고, 연결된 도구가 없거나 이미 완료된 도구만 있는 경우에만 합치기
                if (lastMessage && !lastMessage.isUser) {
                    const hasActiveTools =
                        lastMessage.associatedTools?.some(
                            tool => !tool.isCompleted
                        ) ?? false;

                    // 활성화된 도구가 없을 때만 기존 메시지에 텍스트 추가
                    if (!hasActiveTools) {
                        console.log('[ChatWindow] 기존 AI 메시지에 청크 추가');
                        // 이미 AI 응답이 있으면 내용 업데이트
                        return prev.map((msg, index) => {
                            if (index === prev.length - 1) {
                                const updatedMessage = {
                                    ...msg,
                                    content: msg.content + content,
                                };
                                console.log(
                                    '[ChatWindow] 업데이트된 메시지 길이:',
                                    updatedMessage.content.length
                                );
                                return updatedMessage;
                            }
                            return msg;
                        });
                    }
                }

                // 마지막 메시지가 사용자 메시지이거나, 활성 도구가 있는 AI 메시지일 경우 새 메시지 생성
                console.log('[ChatWindow] 새 AI 메시지 생성');
                return [...prev, botMessage];
            });
        };

        // 도구 사용 완료 함수
        const completeToolUse = (toolName: string) => {
            console.log('[ChatWindow] 도구 사용 완료:', toolName);

            // 보고서 형식 도구인지 확인
            const isReportFormatTool =
                toolName.includes('daily_report_format') ||
                toolName.includes('weekly_report_format');
            if (isReportFormatTool) {
                console.log(
                    '%c[ChatWindow] 보고서 형식 도구 완료 감지:',
                    'background: #ff9800; color: white; font-weight: bold; padding: 2px 5px; border-radius: 3px;',
                    toolName
                );

                // 보고서 형식 도구를 별도로 관리하고 activeTools에서는 제외
                setReportFormatTools(prev => {
                    // 이미 동일한 도구가 있는지 확인
                    const exists = prev.some(tool => tool.name === toolName);

                    // 이미 존재하는 도구인 경우
                    if (exists) {
                        // 이미 완료된 도구인지 확인
                        const existingTool = prev.find(
                            tool => tool.name === toolName
                        );
                        const alreadyCompleted =
                            existingTool?.isCompleted === true;

                        console.log(
                            `%c[ChatWindow] 이미 존재하는 보고서 형식 도구 업데이트:`,
                            'background: #ff9800; color: white; font-weight: bold; padding: 2px 5px; border-radius: 3px;',
                            {
                                name: toolName,
                                alreadyCompleted,
                                currentShowAfterMessage:
                                    existingTool?.showAfterMessage,
                                willSetShowAfterMessage: true,
                            }
                        );

                        // 이미 완료된 도구에 대해 중복 완료 신호가 왔을 때 로그
                        if (alreadyCompleted) {
                            console.log(
                                `%c[ChatWindow] 주의: 이미 완료된 도구에 대한 중복 완료 신호 받음`,
                                'background: #f44336; color: white; font-weight: bold;'
                            );
                        }

                        return prev.map(tool =>
                            tool.name === toolName
                                ? {
                                      ...tool,
                                      isCompleted: true,
                                      showAfterMessage: true, // 도구가 완료되면 AI 메세지 이후에 표시하도록 설정
                                      // 중복 완료 신호에 대한 추가 정보 기록
                                      _duplicateCompletionDetected:
                                          alreadyCompleted,
                                  }
                                : tool
                        );
                    }

                    // 새 도구 추가 - 이 경우는 이상한 상황임
                    console.log(
                        `%c[ChatWindow] 주의: completeToolUse에서 새 보고서 형식 도구 추가 시도:`,
                        'background: #f44336; color: white; font-weight: bold; padding: 2px 5px; border-radius: 3px;',
                        toolName
                    );
                    return [
                        ...prev,
                        {
                            name: toolName,
                            input: {},
                            isCompleted: true,
                            specialType: 'report_format_result',
                            showAfterMessage: true, // 도구가 완료되면 AI 메세지 이후에 표시하도록 설정
                            _createdInCompleteToolUse: true, // 비정상적인 경로로 생성되었음을 표시
                        },
                    ];
                });
            }

            // activeTools 상태 업데이트 (보고서 형식 도구가 아닌 경우에만)
            setActiveTools(prev => {
                // 중복 방지를 위해 이미 있는지 확인
                const exists = prev.some(tool => tool.name === toolName);
                if (exists) {
                    const updatedTools = prev.map(tool =>
                        tool.name === toolName
                            ? { ...tool, isCompleted: true }
                            : tool
                    );

                    // 모든 도구가 완료되었는지 확인
                    const allCompleted = updatedTools.every(
                        tool => tool.isCompleted
                    );
                    console.log(
                        '[ChatWindow] 모든 도구 완료 여부:',
                        allCompleted
                    );

                    return updatedTools;
                } else {
                    // 새 도구 추가 (보고서 형식 도구도 포함)
                    const newTool = {
                        name: toolName,
                        input: {},
                        isCompleted: true,
                    };

                    const updatedTools = [...prev, newTool];

                    // 모든 도구가 완료되었는지 확인
                    const allCompleted = updatedTools.every(
                        tool => tool.isCompleted
                    );
                    console.log(
                        '[ChatWindow] 모든 도구 완료 여부:',
                        allCompleted
                    );

                    return updatedTools;
                }
            });

            // 메시지에 연결된 도구 상태도 업데이트
            setMessages(prev => {
                // 마지막 AI 메시지 찾기
                const lastAiMessageIndex = [...prev]
                    .reverse()
                    .findIndex(msg => !msg.isUser);
                if (lastAiMessageIndex === -1) {
                    console.log('[ChatWindow] AI 메시지가 없어 도구 연결 불가');
                    return prev;
                }

                const actualIndex = prev.length - 1 - lastAiMessageIndex;

                return prev.map((msg, index) => {
                    if (msg.associatedTools && msg.associatedTools.length > 0) {
                        const updatedTools = msg.associatedTools.map(tool =>
                            tool.name === toolName
                                ? {
                                      ...tool,
                                      isCompleted: true,
                                      specialType: isReportFormatTool
                                          ? 'report_format_result'
                                          : tool.specialType,
                                      // showReportFormat은 이벤트 수신 시에만 설정하므로 여기서는 변경하지 않음
                                  }
                                : tool
                        );

                        return {
                            ...msg,
                            associatedTools: updatedTools,
                        };
                    }

                    return msg;
                });
            });
        };

        // 오류 메시지 추가 함수 (외부에서 호출)
        const addErrorMessage = (errorMessage: string) => {
            console.log('[ChatWindow] 오류 메시지 추가:', errorMessage);

            const errorMsg: ChatMessage = {
                content: t('chat.errorMessage', { message: errorMessage }),
                isUser: false,
                timestamp: new Date(),
            };

            setMessages(prev => [...prev, errorMsg]);
            setIsLoading(false);
        };

        // 로딩 상태 변경 함수 (외부에서 호출)
        const setLoadingState = (loading: boolean) => {
            console.log('[ChatWindow] 로딩 상태 변경:', loading);
            setIsLoading(loading);

            // 로딩이 시작되면 스트리밍 상태 설정
            if (loading) {
                isStreamingRef.current = true;
            } else {
                // 로딩이 종료되면 약간의 지연 후 스트리밍 상태 해제
                setTimeout(() => {
                    isStreamingRef.current = false;
                }, 1000);
            }
        };

        // 도구 상태 초기화 함수 - 웹소켓 연결 시 호출
        const resetToolStates = () => {
            console.log('[ChatWindow] 도구 상태 초기화 (웹소켓 연결 시)');
            setActiveTools([]);
            setReportFormatTools([]);
        };

        // 외부에서 접근할 수 있도록 함수 노출
        useImperativeHandle(ref, () => ({
            addAiMessage,
            addErrorMessage,
            setLoadingState,
            addToolUse,
            completeToolUse,
            resetToolStates, // 도구 상태 초기화 함수 추가
        }));

        // 선택된 기능에 따라 적절한 도구 문자열 반환
        const getToolsStringByFeature = (featureId: string): string => {
            switch (featureId) {
                case 'search-note':
                    return 'search_note';
                case 'get-todo':
                    return 'get_todos,get_public_todos,find_user_id';
                case 'create-todo':
                    return 'create_todo';
                case 'create-report':
                    return 'get_data_for_daily_report,get_data_for_weekly_report';
                default:
                    return '';
            }
        };
        
        // 홈 버튼 클릭 시 기능 초기화 함수
        const handleHomeClick = () => {
            setSelectedFeature(null);
            setMessages([]); // 메시지 배열 초기화
            setActiveTools([]); // 활성 도구 초기화
            setReportFormatTools([]); // 보고서 형식 도구 초기화
            // 스크롤 상태 초기화 추가
            setIsUserScrolling(false);
            setIsNearBottom(true);
            setShowScrollToBottom(false);
            // 타임아웃 정리
            if (userScrollTimeoutRef.current) {
                clearTimeout(userScrollTimeoutRef.current);
                userScrollTimeoutRef.current = null;
            }
            // 스트리밍 상태 초기화
            isStreamingRef.current = false;
            // 메시지 개수 초기화
            lastMessageCountRef.current = 0;
            console.log(
                '[ChatWindow] 홈 버튼 클릭, 기능 초기화 (메시지, 도구 상태, 스크롤 상태 초기화)'
            );
        };

        if (!isOpen) return null;

        const handleSendMessage = async (latestMessage?: string) => {
            // 매개변수로 전달된 최신 메시지가 있으면 그것을 사용하고, 없으면 inputValue 사용
            const messageToSend = latestMessage || inputValue;
            if (messageToSend.trim() === '' || isLoading) return;

            console.log('[ChatWindow] 사용자 메시지 입력:', messageToSend);

            // JSON 형식으로 전달된 메타데이터가 있는지 확인
            let actualMessage = messageToSend;
            let reportMetadata: Record<string, unknown> | undefined;

            try {
                // JSON 형식인지 확인하고 파싱
                const parsedMessage: {
                    text?: string;
                    metadata?: Record<string, unknown>;
                } = JSON.parse(messageToSend);
                if (parsedMessage.text && parsedMessage.metadata) {
                    console.log(
                        '[ChatWindow] 메타데이터 포함된 메시지 감지:',
                        parsedMessage.metadata
                    );
                    actualMessage = parsedMessage.text;
                    reportMetadata = parsedMessage.metadata;
                }
            } catch (e) {
                // JSON 파싱 실패 시 원본 메시지 사용
                console.log('[ChatWindow] 일반 텍스트 메시지 사용');
            }

            // 사용자 메시지 추가
            const userMessage: ChatMessage = {
                content: actualMessage,
                isUser: true,
                timestamp: new Date(),
                // 메타데이터가 있으면 추가 속성으로 저장
                metadata: reportMetadata,
            };

            setMessages([...messages, userMessage]);
            setInputValue('');
            setIsLoading(true);
            setIsThinking(true); // 사용자 메시지 전송 후 생각 중 상태 활성화

            // 웹소켓 응답이 없는 경우를 대비해 일정 시간 후 로딩 상태 해제
            const safetyTimeout = setTimeout(() => {
                console.log('[ChatWindow] 안전 타임아웃: 로딩 상태 자동 해제');
                setIsLoading(false);
                setIsThinking(false); // 타임아웃 시 생각 중 상태도 해제

                // 로딩 상태 변경 이벤트 발생
                const loadingStateEvent = new CustomEvent(
                    'ai-loading-state-changed',
                    {
                        detail: { isLoading: false },
                    }
                );
                window.dispatchEvent(loadingStateEvent);
            }, 20000); // 20초 후 타임아웃

            // 새 대화 시작 시 이전 도구 사용 내역 초기화 (보고서 형식 도구 제외)
            setActiveTools(prev => {
                const reportFormatTools = prev.filter(
                    tool =>
                        tool.name.includes('daily_report_format') ||
                        tool.name.includes('weekly_report_format')
                );
                return reportFormatTools; // 보고서 형식 도구만 유지
            });

            // 두 가지 분기로 처리:
            // 1. 외부 콜백이 있으면 외부 콜백만 사용 (외부에서 AI 요청 처리)
            if (onSendMessage) {
                console.log(
                    '[ChatWindow] 외부 콜백으로 메시지 처리:',
                    messageToSend
                );
                onSendMessage(messageToSend);
            }
            // 2. 외부 콜백이 없으면 내부 AI 요청 처리
            else {
                try {
                    await defaultAiRequestManager.connect();

                    // UI 콜백 설정 - AiRequestManager에 전달
                    defaultAiRequestManager.setUICallbacks({
                        onTextUpdate: (message, isNewMessage) => {
                            // 메시지 버블 업데이트를 위한 콜백
                            console.log(
                                `[ChatWindow] AI 메시지 버블에 전달: ${message.length}자`,
                                isNewMessage ? '(새 메시지)' : ''
                            );
                            addAiMessage(message, isNewMessage);
                        },
                        onToolUseReceived: toolMessage => {
                            // 도구 사용 메시지 처리
                            if (toolMessage) {
                                const parsedTool =
                                    parseToolUseMessage(toolMessage);
                                if (parsedTool) {
                                    addToolUse(parsedTool);
                                }
                            }
                        },
                        onStateUpdate: tools => {
                            // 활성 도구 목록 상태 업데이트
                            console.log(
                                '[ChatWindow] 도구 상태 업데이트 수신:',
                                tools.length > 0 ? tools[0].name : '없음'
                            );
                            setActiveTools(tools);
                        },
                    });

                    // 선택된 기능에 따라 적절한 도구 문자열 배열 생성
                    console.log('[ChatWindow] 선택된 기능:', selectedFeature);
                    const toolsString = selectedFeature ? getToolsStringByFeature(selectedFeature) : '';
                    console.log('[ChatWindow] 생성된 도구 문자열:', toolsString);
                    
                    await defaultAiRequestManager.sendUserMessage(
                        messageToSend,
                        user?.id,
                        user?.name,
                        user?.timezone,
                        user?.language,
                        toolsString
                    );
                    console.log('[ChatWindow] AI 메시지 전송 완료');
                    console.log('toolsString:', toolsString);
                    clearTimeout(safetyTimeout);
                } catch (err) {
                    console.error('[ChatWindow] AI 전송 오류', err);
                    addErrorMessage(t('chat.aiRequestError'));
                    clearTimeout(safetyTimeout);
                    setIsLoading(false);
                    setIsThinking(false);
                }
            }
        };

        // 채팅 인터페이스 렌더링
        const renderChatInterface = () => {
            // 디버깅을 위해 현재 메시지 상태와 연결된 도구 로깅
            console.log(
                '[ChatWindow] 렌더링 시 메시지 상태:',
                messages.map((msg, idx) => ({
                    index: idx,
                    isUser: msg.isUser,
                    contentPreview: msg.content.substring(0, 30) + '...',
                    toolsCount: msg.associatedTools?.length || 0,
                    toolNames: msg.associatedTools?.map(t => t.name) || [],
                }))
            );
            // console.log('[ChatWindow] 활성 도구 목록:', activeTools);

            return (
                <>
                    {messages.length === 0 ? (
                        <Flex
                            align="center"
                            justify="center"
                            style={{ height: '100%' }}
                        ></Flex>
                    ) : (
                        <ScrollArea
                            className={classes.chatMessagesContainer} // className 변경
                            viewportRef={scrollAreaRef}
                            onScrollPositionChange={handleScroll}
                            onWheel={handleWheel} // 휠 이벤트 핸들러 직접 연결
                            style={{
                                flexGrow: 1,
                                height: 'calc(100% - 72px)', // 헤더와 입력 영역을 뺀 높이
                                position: 'relative',
                            }}
                        >
                            <Stack gap="xs">
                                {/* 스타일 정의 */}
                                {/* 스타일은 ChatWindow.css로 이동 */}

                                {messages.map((message, index) => {
                                    // 각 메시지의 연결된 도구 로깅
                                    // console.log(`[ChatWindow] 메시지[${index}] 렌더링:`, {
                                    //     isUser: message.isUser,
                                    //     hasTools: !!(message.associatedTools && message.associatedTools.length > 0),
                                    //     toolsCount: message.associatedTools?.length || 0,
                                    //     toolNames: message.associatedTools?.map(t => t.name) || []
                                    // });

                                    return (
                                        <React.Fragment
                                            key={`msg-group-${index}`}
                                        >
                                            {/* 사용자 메시지 표시 */}
                                            {message.isUser && (
                                                <UserMessageBubble
                                                    key={`user-${index}`}
                                                    content={message.content}
                                                    timestamp={
                                                        message.timestamp
                                                    }
                                                />
                                            )}

                                            {/* 생각 중 애니메이션 표시 (마지막 사용자 메시지 다음에 표시) */}
                                            {isThinking &&
                                                index === messages.length - 1 &&
                                                message.isUser && (
                                                    <Flex
                                                        key={`thinking-after-${index}`}
                                                        align="center"
                                                        className={
                                                            classes.thinkingContainer
                                                        } // className 변경
                                                    >
                                                        <Text
                                                            size="sm"
                                                            color="dimmed"
                                                            style={{
                                                                fontWeight: 500,
                                                            }}
                                                        >
                                                            {t('chat.thinking')}
                                                            <span
                                                                className={
                                                                    classes.thinkingDot
                                                                }
                                                            >
                                                                .
                                                            </span>
                                                            <span
                                                                className={
                                                                    classes.thinkingDot
                                                                }
                                                            >
                                                                .
                                                            </span>
                                                            <span
                                                                className={
                                                                    classes.thinkingDot
                                                                }
                                                            >
                                                                .
                                                            </span>
                                                        </Text>
                                                    </Flex>
                                                )}

                                            {/* AI 메시지 표시 */}
                                            {!message.isUser && (
                                                <React.Fragment>
                                                    <AiMessageBubble
                                                        key={`ai-${index}`}
                                                        content={
                                                            message.content
                                                        }
                                                        timestamp={
                                                            message.timestamp
                                                        }
                                                    />

                                                    {/* 보고서 생성 버튼 표시 - daily_report_format 또는 weekly_report_format 도구가 사용된 경우에만 표시 */}
                                                    {/* 로그는 코드에서 출력하지 않고 조건을 확인하는 용도로 사용 */}
                                                    {reportFormatTools.some(
                                                        tool =>
                                                            (tool.name ===
                                                                'daily_report_format' ||
                                                                tool.name ===
                                                                    'weekly_report_format') &&
                                                            tool.showAfterMessage &&
                                                            tool.isCompleted ===
                                                                true
                                                    ) &&
                                                        !message.isUser &&
                                                        index ===
                                                            messages.length -
                                                                1 && (
                                                            <>
                                                            {/* 보고서 생성 결과 컴포넌트 임시 비활성화 */}
                                                            </>
                                                        )}
                                                </React.Fragment>
                                            )}

                                            {/* AI 메시지에 연결된 도구가 있으면 바로 아래에 표시 */}
                                            {!message.isUser &&
                                                message.associatedTools &&
                                                message.associatedTools.length >
                                                    0 && (
                                                    <Box
                                                        key={`tools-${index}`}
                                                        mt={-2}
                                                        ml={4}
                                                        className={
                                                            classes.chatMessageTools
                                                        } // className 변경
                                                    >
                                                        <ActiveToolsList
                                                            tools={
                                                                message.associatedTools
                                                            }
                                                        />
                                                    </Box>
                                                )}
                                        </React.Fragment>
                                    );
                                })}
                            </Stack>

                            {/* 스크롤 하단으로 이동 버튼 - 사용자가 스크롤 중이고 하단에서 멀리 있을 때만 표시 */}
                            {isUserScrolling && !isNearBottom && (
                                <ActionIcon
                                    variant="filled"
                                    color="blue"
                                    radius="xl"
                                    size="md"
                                    style={{
                                        position: 'absolute',
                                        bottom: '20px',
                                        right: '20px',
                                        zIndex: 10,
                                        boxShadow:
                                            '0 2px 4px rgba(0, 0, 0, 0.2)',
                                    }}
                                    onClick={() => {
                                        if (scrollAreaRef.current) {
                                            scrollAreaRef.current.scrollTop =
                                                scrollAreaRef.current.scrollHeight;
                                            setIsUserScrolling(false);
                                            setIsNearBottom(true);
                                            console.log(
                                                '[ChatWindow] 하단 스크롤 버튼 클릭'
                                            );
                                        }
                                    }}
                                >
                                    <IconChevronDown size={18} />
                                </ActionIcon>
                            )}
                        </ScrollArea>
                    )}
                </>
            );
        };

        return (
            <Paper
                shadow="xl"
                radius="16px"
                className={classes.chatWindow} // className 변경
                style={{
                    boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.25)',
                }}
            >
                <Flex
                    direction="column"
                    style={{
                        width: '100%',
                        height: '100%',
                        position: 'relative',
                    }}
                >
                    <Flex
                        style={{
                            width: '100%',
                            boxSizing: 'border-box',
                            flexShrink: 0,
                        }}
                    >
                        {/* 왼쪽 헤더 - 접힌 상태에 따라 클래스 변경 */}
                        <Flex
                            p="xs"
                            align="center"
                            justify={
                                leftSectionCollapsed
                                    ? 'center'
                                    : 'space-between'
                            }
                            className={
                                leftSectionCollapsed
                                    ? classes.chatHeaderLeftClosed
                                    : classes.chatHeaderLeft
                            } // className 변경
                            style={{ flexShrink: 0 }}
                        >
                            {leftSectionCollapsed ? (
                                // <ActionIcon
                                //     variant="subtle"
                                //     color="gray"
                                //     onClick={() =>
                                //         setLeftSectionCollapsed(
                                //             !leftSectionCollapsed
                                //         )
                                //     }
                                //     title="펼치기"
                                // >
                                //     <IconLayoutSidebarRight
                                //         size={18}
                                //         color="#868E96"
                                //     />
                                // </ActionIcon>
                                <div
                                    style={{ width: '18px', height: '18px' }}
                                ></div>
                            ) : (
                                <>
                                    <Group gap={8} align="center"></Group>
                                    <ActionIcon
                                        variant="subtle"
                                        color="gray"
                                        onClick={() =>
                                            setLeftSectionCollapsed(
                                                !leftSectionCollapsed
                                            )
                                        }
                                        title="접기"
                                    >
                                        <IconLayoutSidebar
                                            size={18}
                                            color="#868E96"
                                        />
                                    </ActionIcon>
                                </>
                            )}
                        </Flex>

                        {/* 오른쪽 헤더 - 접힌 상태에 따라 클래스 변경 */}
                        <Flex
                            p="xs"
                            justify="flex-end"
                            align="center"
                            className={
                                leftSectionCollapsed
                                    ? classes.chatHeaderRightClosed
                                    : classes.chatHeaderRight
                            } // className 변경
                        >
                            {/* 이전으로 버튼 - 기능 선택 후에만 표시 */}
                            {selectedFeature && (
                                <Button
                                    variant="subtle"
                                    color="gray"
                                    onClick={handleHomeClick}
                                    style={{
                                        marginRight: 'auto',
                                        paddingLeft: '2px',
                                    }}
                                    size="xs"
                                    px="xs"
                                >
                                    <Group gap={2} align="center">
                                        <IconChevronLeft
                                            size={16}
                                            color="#228BE6"
                                        />
                                        <span style={{ color: '#228BE6' }}>
                                            {t('chat.backToPrevious')}
                                        </span>
                                    </Group>
                                </Button>
                            )}

                            <ActionIcon
                                variant="subtle"
                                color="gray"
                                onClick={onClose}
                            >
                                <IconX size={18} color="#212529" />
                            </ActionIcon>
                        </Flex>
                    </Flex>

                    <Flex
                        style={{
                            flex: 1,
                            height: 'calc(100% - 56px)',
                            minHeight: 0,
                            position: 'relative',
                        }}
                    >
                        {/* 왼쪽 섹션 - 채팅 히스토리 */}
                        <ChatLeftSection
                            isCollapsed={leftSectionCollapsed}
                            onToggleCollapse={() =>
                                setLeftSectionCollapsed(!leftSectionCollapsed)
                            }
                            style={{
                                height: '100%',
                                width: leftSectionCollapsed ? '56px' : '318px',
                                flexShrink: 0,
                                position: 'relative',
                            }}
                        />

                        {/* 오른쪽 섹션 - 채팅 메시지 */}
                        <ChatRightSection
                            messages={messages}
                            inputValue={inputValue}
                            setInputValue={setInputValue}
                            handleSendMessage={message => {
                                void handleSendMessage(message);
                            }}
                            isLoading={isLoading}
                            renderChatInterface={renderChatInterface}
                            onMessageReceived={addAiMessage}
                            selectedFeature={selectedFeature}
                            setSelectedFeature={setSelectedFeature}
                            style={{
                                width: leftSectionCollapsed ? '744px' : '482px',
                                height: '100%',
                                flexShrink: 0,
                                position: 'relative',
                            }}
                        />
                    </Flex>
                </Flex>
            </Paper>
        );
    }
);

// 컴포넌트 이름 설정
ChatWindow.displayName = 'ChatWindow';

// 외부에서 접근할 수 있도록 함수 노출
export type { ChatMessage, ToolInfo };
export default ChatWindow;
